import { html, type Html, View } from 'rune-ts';
import klass from './Tooltip.module.scss';
import tippy from 'tippy.js';
import { makeInfoIcon } from '../Icon';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';

type TooltipSizeType = 'x_small' | 'small' | 'medium';

type TooltipSizes = {
  readonly [K in TooltipSizeType]: K;
};

type Variant = 'studio';

export interface TooltipProps {
  isOpen?: boolean;
  id: string;
  content: string | Html | View;
  triggerClass?: string;
  contentClass?: string;

  /**
   * @default 'medium'
   */
  size?: TooltipSizeType;

  variant?: Variant;
}

export class Tooltip extends View<TooltipProps> {
  private readonly trigger_id: string;

  static Sizes: TooltipSizes = {
    x_small: klass.x_small,
    small: klass.small,
    medium: klass.medium,
  } as const;

  constructor(data: TooltipProps) {
    super(data);
    this.trigger_id = `${this.data.id}_trigger`;
  }

  protected override template(data: TooltipProps): Html {
    const { id, content, triggerClass = '', contentClass = '' } = data;

    return html`
      <div
        class="${classes(klass.tooltip, {
          [klass.studio]: this.data.variant === 'studio',
        })}"
        data-size="${this.size}"
      >
        ${makeInfoIcon({
          id: this.trigger_id,
          klass: `${klass.trigger} ${triggerClass} ${Tooltip.Sizes[this.size]}`,
        })}
        <script id="${id}" type="text/html">
          <div class="${klass.content} ${contentClass} ${font('14_medium')}">${content}</div>
        </script>
      </div>
    `;
  }

  override onMount() {
    this.initTooltip();
  }

  initTooltip() {
    let tooltipTimeout: ReturnType<typeof setTimeout> | null = null;

    const { id } = this.data;
    const content = document.getElementById(id);
    tippy(`#${this.trigger_id}`, {
      content: content!.innerHTML,
      trigger: 'mouseenter click',
      allowHTML: true,
      appendTo: 'parent',
      placement: 'bottom-start',
      offset: [0, 8],
      popperOptions: {
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              padding: 16,
              boundariesElement: 'viewport',
            },
          },
        ],
      },
      hideOnClick: true,
      onShow(instance) {
        instance.reference.classList.add(klass.active);

        if (tooltipTimeout) {
          clearTimeout(tooltipTimeout);
        }

        tooltipTimeout = setTimeout(() => {
          instance.hide();
        }, 10000);
      },
      onHidden(instance) {
        instance.reference.classList.remove(klass.active);
      },
    });
  }

  get size() {
    return this.data.size || 'medium';
  }
}
